import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  TriangleAlert,
  Search,
  ChevronRight,
  X,
  Check,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useRun } from "@/context/RunContext";
import { compactINR, submitCaseDecision, fetchCaseDecision, fetchTeams } from "@/lib/api";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Kpi = ({ value, label, tone = "default", testid }) => (
  <div data-testid={testid}>
    <p
      className={`font-mono text-3xl ${
        tone === "warn"
          ? "text-amber-700"
          : tone === "good"
          ? "text-emerald-700"
          : "text-zinc-900"
      }`}
    >
      {value}
    </p>
    <p className="mt-1 text-[11px] uppercase tracking-wide text-zinc-400">{label}</p>
  </div>
);

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return compactINR(v);
  return String(v);
};

// ---------------------------------------------------------------------------
// Case detail slide-out panel
// ---------------------------------------------------------------------------

function CaseDetail({ c, onClose }) {
  const { runDecisions, runId, refreshDecisions } = useRun();
  const [decision, setDecision] = useState(null);
  const [owner, setOwner] = useState("");
  const [comment, setComment] = useState("");
  const [decidedBy, setDecidedBy] = useState("");
  const [savedDecision, setSavedDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [teams, setTeams] = useState([]);
  const [actionType, setActionType] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [assignedTeam, setAssignedTeam] = useState(c.investigation?.suggested_owner || "");
  const [escalatedTeam, setEscalatedTeam] = useState("");
  const [escalatedPerson, setEscalatedPerson] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchTeams().then(setTeams).catch(() => {});
  }, []);

  useEffect(() => {
    const existing = runDecisions[c.case_id];
    if (existing) {
      setSavedDecision(existing);
      setDecision(existing.decision);
      setOwner(existing.owner || "");
      setComment(existing.comment || "");
      setDecidedBy(existing.decided_by || "");
    } else {
      fetchCaseDecision(c.case_id)
        .then((d) => {
          setSavedDecision(d);
          setDecision(d.decision);
          setOwner(d.owner || "");
          setComment(d.comment || "");
          setDecidedBy(d.decided_by || "");
        })
        .catch(() => {
          setSavedDecision(null);
          setEditMode(true);
        });
    }
  }, [c.case_id, runDecisions]);

  const handleSubmit = async () => {
    if (!decision || !decidedBy) return;
    setLoading(true);
    try {
      const payload = {
        decision,
        owner,
        comment,
        decided_by: decidedBy,
      };

      if (decision === "CORRECT") {
        payload.corrective_action = {
          action_type: actionType,
          description: actionDescription,
          assigned_team: assignedTeam,
        };
      }

      if (decision === "REJECT") {
        payload.escalation = {
          escalated_to_team: escalatedTeam,
          escalated_to_person: escalatedPerson,
          reason: rejectionReason,
        };
      }

      const saved = await submitCaseDecision(c.case_id, payload);
      setSavedDecision(saved);
      setEditMode(false);
      await refreshDecisions(runId);
      toast.success(`Decision ${decision}D successfully`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit decision");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDecision = () => {
    setEditMode(true);
  };

  const isFormValid = () => {
    if (!decision || !decidedBy) return false;
    if (decision === "CORRECT" && (!actionType || !actionDescription || !assignedTeam)) return false;
    if (decision === "REJECT" && (!escalatedTeam || !rejectionReason)) return false;
    return true;
  };

  const decisionBadgeClass = (dec) => {
    switch (dec) {
      case "APPROVE": return "bg-emerald-50 border-emerald-200 text-emerald-700";
      case "REJECT": return "bg-red-50 border-red-200 text-red-700";
      case "CORRECT": return "bg-amber-50 border-amber-200 text-amber-700";
      default: return "bg-zinc-100 border-zinc-200 text-zinc-600";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/20 backdrop-blur-sm">
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-zinc-200 bg-white shadow-2xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-4 z-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-wide text-zinc-400">Exception Case</p>
            <h2 className="mt-0.5 font-heading text-lg font-medium text-zinc-900">{c.invoice_id || c.transaction_key}</h2>
            {c.reconciliation?.exception_type && (
              <span className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-amber-700">
                <TriangleAlert className="h-3 w-3" />
                {c.reconciliation.exception_type}
              </span>
            )}
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:text-zinc-900 focus-visible:ring-2 focus-visible:ring-blue-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-0 divide-y divide-zinc-100 px-6 py-6">
          <section className="pb-6">
            <h3 className="font-mono text-[11px] uppercase tracking-wide text-zinc-400">Reconciliation Evidence</h3>
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4">
              {[["Status", c.reconciliation?.status], ["Exception Type", c.reconciliation?.exception_type], ["Rules Triggered", c.reconciliation?.rules_triggered], ["Sources Present", c.reconciliation?.sources_present], ["ERP Amount", fmt(c.reconciliation?.erp_amount)], ["CRM Amount", fmt(c.reconciliation?.crm_amount)], ["Treasury Amount", fmt(c.reconciliation?.treasury_amount)], ["Variance", fmt(c.reconciliation?.variance)]].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[11px] uppercase tracking-wide text-zinc-400">{k}</dt>
                  <dd className="mt-0.5 font-mono text-sm text-zinc-900">{fmt(v)}</dd>
                </div>
              ))}
            </dl>
            {c.reconciliation?.evidence && (
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400">Evidence</p>
                <p className="mt-1.5 rounded-md border border-zinc-100 bg-zinc-50 p-3 font-mono text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed">
                  {c.reconciliation.evidence}
                </p>
              </div>
            )}
          </section>

          {/* Source Truth Validation */}
          {(() => {
            const st = c.source_truth;
            const isUnavailable = !st || st.status === "SOURCE_TRUTH_UNAVAILABLE";
            return (
              <section className="py-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-mono text-[11px] uppercase tracking-wide text-zinc-400">
                    Source Truth Validation
                  </h3>
                  <span
                    className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide ${
                      st?.status === "VALIDATED" || st?.status === "AVAILABLE"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : st?.status === "PARTIAL_VALIDATION"
                        ? "border-amber-200 bg-amber-50 text-amber-700"
                        : "border-zinc-200 bg-zinc-100 text-zinc-500"
                    }`}
                  >
                    {st?.status === "VALIDATED" || st?.status === "AVAILABLE"
                      ? "Validated"
                      : st?.status === "PARTIAL_VALIDATION"
                      ? "Partial"
                      : "Unavailable"}
                  </span>
                </div>

                {isUnavailable && (
                  <p className="mt-3 font-mono text-xs italic text-zinc-400">
                    {st?.validation_summary || "Invoice document could not be retrieved."}
                  </p>
                )}

                {!isUnavailable && (
                  <>
                    <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3">
                      {st.document_reference && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Document</dt>
                          <dd className="mt-0.5 flex items-center gap-2">
                            <span className="font-mono text-sm text-zinc-900">{st.document_reference}</span>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${c.invoice_id}/content`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-0.5 font-mono text-xs text-blue-600 underline decoration-blue-200 underline-offset-2 transition-colors hover:decoration-blue-600"
                            >
                              View ↗
                            </a>
                          </dd>
                        </div>
                      )}
                      {st.confidence != null && (
                        <div>
                          <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Confidence</dt>
                          <dd className="mt-0.5 font-mono text-sm text-zinc-900">
                            {typeof st.confidence === "number"
                              ? `${Math.round(st.confidence * 100)}%`
                              : fmt(st.confidence)}
                          </dd>
                        </div>
                      )}
                    </dl>

                    {st.field_comparisons?.length > 0 && (
                      <div className="mt-5">
                        <p className="text-[11px] uppercase tracking-wide text-zinc-400">Field Comparisons</p>
                        <table className="mt-2 w-full text-left">
                          <thead>
                            <tr className="border-b border-zinc-100">
                              {["Field", "Source Truth", "ERP", "CRM", "Treasury"].map((h) => (
                                <th key={h} className="pb-1.5 pr-3 font-mono text-[10px] uppercase tracking-wide text-zinc-400">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {st.field_comparisons.map((fc, i) => (
                              <tr key={i} className="border-b border-zinc-50">
                                <td className="py-2 pr-3 font-mono text-xs text-zinc-700">{fc.field}</td>
                                <td className="py-2 pr-3 font-mono text-xs text-zinc-900">{fmt(fc.source_truth_value)}</td>
                                {["erp_match", "crm_match", "treasury_match"].map((key) => (
                                  <td key={key} className="py-2 pr-3">
                                    {fc[key] === true ? (
                                      <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={3} />
                                    ) : fc[key] === false ? (
                                      <TriangleAlert className="h-3.5 w-3.5 text-amber-600" />
                                    ) : (
                                      <span className="font-mono text-xs text-zinc-300">—</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {st.validation_summary && (
                      <div className="mt-5">
                        <p className="text-[11px] uppercase tracking-wide text-zinc-400">Validation Summary</p>
                        <p className="mt-1.5 rounded-md border border-zinc-100 bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-700 whitespace-pre-wrap">
                          {st.validation_summary}
                        </p>
                      </div>
                    )}

                    {(st.matching_sources?.length > 0 || st.conflicting_sources?.length > 0) && (
                      <dl className="mt-5 grid grid-cols-2 gap-x-8 gap-y-3">
                        {st.matching_sources?.length > 0 && (
                          <div>
                            <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Matching Sources</dt>
                            <dd className="mt-0.5 font-mono text-xs text-emerald-700">
                              {st.matching_sources.join(", ")}
                            </dd>
                          </div>
                        )}
                        {st.conflicting_sources?.length > 0 && (
                          <div>
                            <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Conflicting Sources</dt>
                            <dd className="mt-0.5 font-mono text-xs text-amber-700">
                              {st.conflicting_sources.join(", ")}
                            </dd>
                          </div>
                        )}
                      </dl>
                    )}
                  </>
                )}
              </section>
            );
          })()}

          <section className="py-6">
            <h3 className="font-mono text-[11px] uppercase tracking-wide text-zinc-400">AI Investigation</h3>
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4">
              {[["Root Cause", c.investigation?.root_cause], ["Suggested Owner", c.investigation?.suggested_owner], ["Confidence", c.investigation?.confidence]].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[11px] uppercase tracking-wide text-zinc-400">{k}</dt>
                  <dd className="mt-0.5 font-mono text-sm text-zinc-900">{fmt(v)}</dd>
                </div>
              ))}
            </dl>
            {c.investigation?.evidence && (
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400">Investigation Evidence</p>
                <p className="mt-1.5 rounded-md border border-zinc-100 bg-zinc-50 p-3 font-mono text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed">{c.investigation.evidence}</p>
              </div>
            )}
            {c.investigation?.recommended_action && (
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400">Recommended Action</p>
                <p className="mt-1.5 rounded-md border border-amber-100 bg-amber-50 p-3 font-mono text-xs text-amber-900 whitespace-pre-wrap leading-relaxed">{c.investigation.recommended_action}</p>
              </div>
            )}
          </section>

          <section className="pt-6">
            <h3 className="font-mono text-[11px] uppercase tracking-wide text-zinc-400">Review</h3>

            {savedDecision && !editMode ? (
              <div className="mt-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-700" />
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-xs uppercase ${decisionBadgeClass(savedDecision.decision)}`}>
                      {savedDecision.decision}D
                    </span>
                  </div>
                  <button onClick={handleChangeDecision} className="text-xs text-zinc-500 hover:text-zinc-900">Change decision</button>
                </div>
                <div className="space-y-2 text-sm">
                  {savedDecision.owner && <p><span className="text-zinc-400">Owner:</span> {savedDecision.owner}</p>}
                  <p><span className="text-zinc-400">Decided by:</span> {savedDecision.decided_by}</p>
                  <p className="font-mono text-xs text-zinc-500">{savedDecision.decided_at ? new Date(savedDecision.decided_at).toLocaleString("en-IN") : ""}</p>
                  {savedDecision.comment && <p className="mt-3 rounded-md border border-zinc-100 bg-zinc-50 p-3 font-mono text-xs text-zinc-700 whitespace-pre-wrap">"{savedDecision.comment}"</p>}
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-zinc-600">Requires Approval:</p>
                  <span className="font-mono text-sm text-zinc-900">{c.review?.requires_human_approval ? "Yes" : "No"}</span>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-zinc-400">Decision *</label>
                  <div className="mt-2 flex gap-2">
                    {["APPROVE", "REJECT", "CORRECT"].map((opt) => (
                      <button key={opt} onClick={() => setDecision(opt)} className={`px-3 py-1.5 rounded-md font-mono text-xs transition-colors ${decision === opt ? "bg-zinc-900 text-white" : "border border-zinc-300 text-zinc-600 hover:border-zinc-400"}`}>{opt}</button>
                    ))}
                  </div>
                </div>

                {decision === "CORRECT" && (
                  <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
                    <p className="text-xs font-medium text-amber-900">Corrective Action Details</p>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Action Type *</label>
                      <select value={actionType} onChange={(e) => setActionType(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono">
                        <option value="">Select action type...</option>
                        <option value="REVERSE_DUPLICATE">Reverse Duplicate in CRM</option>
                        <option value="UPDATE_ERP">Update ERP Record</option>
                        <option value="CREATE_CREDIT_NOTE">Create Credit Note</option>
                        <option value="ADJUST_TREASURY">Adjust Treasury Entry</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Assigned Team *</label>
                      <select value={assignedTeam} onChange={(e) => setAssignedTeam(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono">
                        <option value="">Select team...</option>
                        {teams.filter(t => t.type === "CORRECTIVE" || t.type === "BOTH").map((t) => (<option key={t.team_id} value={t.name}>{t.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Description *</label>
                      <textarea value={actionDescription} onChange={(e) => setActionDescription(e.target.value)} placeholder="What needs to be corrected..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" rows={3} />
                    </div>
                  </div>
                )}

                {decision === "REJECT" && (
                  <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-xs font-medium text-red-900">Escalation Details</p>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Escalate to Team *</label>
                      <select value={escalatedTeam} onChange={(e) => setEscalatedTeam(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono">
                        <option value="">Select team...</option>
                        {teams.filter(t => t.type === "ESCALATION" || t.type === "BOTH").map((t) => (<option key={t.team_id} value={t.name}>{t.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Escalate to Person</label>
                      <input type="text" value={escalatedPerson} onChange={(e) => setEscalatedPerson(e.target.value)} placeholder="Optional specific person..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-zinc-600">Reason *</label>
                      <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Why is AI recommendation incorrect..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" rows={3} />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-zinc-400">Owner</label>
                  <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Team or person responsible..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-zinc-400">Comment</label>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional notes..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" rows={3} />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-zinc-400">Decided by *</label>
                  <input type="text" value={decidedBy} onChange={(e) => setDecidedBy(e.target.value)} placeholder="Your name..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" />
                </div>

                <button onClick={handleSubmit} disabled={!isFormValid() || loading} className="w-full rounded-md bg-zinc-900 px-4 py-2 font-mono text-sm text-white transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-40">
                  {loading ? "Submitting..." : "Submit Decision"}
                </button>
              </div>
            )}
          </section>
        </div>
      </motion.aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live review cases table (Intugleflow output)
// ---------------------------------------------------------------------------

function LiveReviewTable({ cases, periodId, period, summary }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const { runDecisions, runId, refreshDecisions } = useRun();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter(
      (c) =>
        (c.invoice_id || "").toLowerCase().includes(q) ||
        (c.transaction_key || "").toLowerCase().includes(q) ||
        (c.reconciliation?.exception_type || "").toLowerCase().includes(q) ||
        (c.investigation?.root_cause || "").toLowerCase().includes(q) ||
        (c.investigation?.suggested_owner || "").toLowerCase().includes(q)
    );
  }, [cases, query]);

  const selectedCase = selected ? cases.find((c) => c.case_id === selected) : null;

  return (
    <div className="max-w-5xl" data-testid="reconciliation-page">
      <Link
        to="/"
        data-testid="back-to-pipeline"
        className="inline-flex items-center gap-2 text-sm text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
      >
        <ArrowLeft className="h-4 w-4" /> Back to close run
      </Link>

      <div className="mt-8 flex flex-wrap items-baseline justify-between gap-4 border-b border-zinc-200 pb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">
            Stage 02 · Reconciliation · {period?.close_type}
          </p>
          <h1 className="mt-3 font-heading text-3xl font-medium tracking-tight text-zinc-900 sm:text-4xl">
            Human Review Queue
          </h1>
          <p className="mt-2 text-sm text-zinc-500">
            {period?.range} · AI investigation complete
          </p>
        </div>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 font-mono text-xs text-amber-800">
          {cases.length} case{cases.length !== 1 ? "s" : ""} pending review
        </span>
      </div>

      {/* KPIs derived from summary (if available) or computed from cases */}
      <div
        className="grid grid-cols-2 gap-x-10 gap-y-8 border-b border-zinc-200 py-10 sm:grid-cols-4"
        data-testid="kpi-strip"
      >
        <Kpi
          value={summary?.total_transactions ?? cases.length}
          label="Total Invoices"
          testid="kpi-total"
        />
        <Kpi
          value={summary?.reconciled_count ?? "—"}
          label="Auto-Reconciled"
          tone="good"
          testid="kpi-reconciled"
        />
        <Kpi
          value={summary?.exception_count ?? cases.length}
          label="Exceptions"
          tone="warn"
          testid="kpi-exceptions"
        />
        <Kpi
          value={cases.filter((c) => c.review?.requires_human_approval).length}
          label="Need Approval"
          tone="warn"
          testid="kpi-need-approval"
        />
      </div>

      {/* Search bar */}
      <div className="flex items-center justify-between gap-4 border-b border-zinc-200 py-5">
        <p className="font-mono text-xs uppercase tracking-wide text-zinc-400">
          Intugleflow · {summary?.investigated_count ?? cases.length} exception
          {(summary?.investigated_count ?? cases.length) !== 1 ? "s" : ""} investigated
        </p>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search invoice, exception, owner…"
            className="h-9 border-zinc-200 bg-white pl-9 font-mono text-xs placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-blue-600"
          />
        </div>
      </div>

      {/* Table */}
      <table className="mt-2 w-full border-collapse text-left" data-testid="reconciliation-table">
        <thead>
          <tr>
            {[
              "Invoice",
              "Exception Type",
              "ERP",
              "CRM",
              "Treasury",
              "Variance",
              "Root Cause",
              "Owner",
              "Confidence",
              "Decision",
              "",
            ].map((h) => (
              <th
                key={h}
                className="border-b border-zinc-200 px-3 py-4 text-[11px] font-medium uppercase tracking-wide text-zinc-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((c, i) => (
            <motion.tr
              key={c.case_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              data-testid={`row-${c.invoice_id}`}
              onClick={() => setSelected(c.case_id)}
              className="group cursor-pointer hover:bg-zinc-50"
            >
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-sm font-medium text-zinc-900">
                {c.invoice_id || c.transaction_key}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5">
                {c.reconciliation?.exception_type ? (
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs text-amber-700">
                    <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                    {c.reconciliation.exception_type}
                  </span>
                ) : (
                  <span className="font-mono text-xs text-zinc-400">—</span>
                )}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-sm text-zinc-900">
                {fmt(c.reconciliation?.erp_amount)}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-sm text-zinc-900">
                {fmt(c.reconciliation?.crm_amount)}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-sm text-zinc-900">
                {fmt(c.reconciliation?.treasury_amount)}
              </td>
              <td
                className={`border-b border-zinc-100 px-3 py-3.5 font-mono text-sm ${
                  c.reconciliation?.variance ? "text-amber-700" : "text-zinc-900"
                }`}
              >
                {fmt(c.reconciliation?.variance)}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 max-w-[180px]">
                <p className="truncate font-mono text-xs text-zinc-600">
                  {c.investigation?.root_cause || "—"}
                </p>
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-xs text-zinc-600">
                {c.investigation?.suggested_owner || "—"}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 font-mono text-xs text-zinc-600">
                {fmt(c.investigation?.confidence)}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5">
                {(() => {
                  const d = runDecisions[c.case_id];
                  if (!d) {
                    return <span className="font-mono text-[10px] uppercase text-zinc-300">Pending</span>;
                  }
                  const colorMap = {
                    APPROVE: "bg-emerald-50 border-emerald-200 text-emerald-700",
                    REJECT: "bg-red-50 border-red-200 text-red-700",
                    CORRECT: "bg-amber-50 border-amber-200 text-amber-700"
                  };
                  return (
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${colorMap[d.decision]}`}>
                      {d.decision}
                    </span>
                  );
                })()}
              </td>
              <td className="border-b border-zinc-100 px-3 py-3.5 text-right">
                <ChevronRight className="ml-auto h-4 w-4 text-zinc-300 transition-colors duration-150 group-hover:text-zinc-900" />
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>

      {!filtered.length && (
        <p className="py-10 text-sm text-zinc-500" data-testid="no-results">
          No cases match this search.
        </p>
      )}

      <p className="mt-6 font-mono text-xs text-zinc-400">
        <span data-testid="row-count">{filtered.length}</span> of {cases.length} cases ·{" "}
        {period?.close_type} · {period?.range}
      </p>

      <AnimatePresence>
        {selectedCase && (
          <CaseDetail c={selectedCase} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — no active run yet
// ---------------------------------------------------------------------------

function NoRunState() {
  return (
    <div className="max-w-5xl" data-testid="reconciliation-page">
      <Link
        to="/"
        data-testid="back-to-pipeline"
        className="inline-flex items-center gap-2 text-sm text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
      >
        <ArrowLeft className="h-4 w-4" /> Back to close run
      </Link>
      <div className="mt-24 flex flex-col items-center text-center">
        <p className="font-mono text-sm text-zinc-500">No active reconciliation run.</p>
        <p className="mt-2 text-sm text-zinc-400">
          Go to{" "}
          <Link to="/" className="underline decoration-zinc-300 hover:decoration-zinc-900">
            Close Run
          </Link>{" "}
          to start a new reconciliation.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export default function Reconciliation() {
  const { reviewCases, runSummary, periodId, period } = useRun();

  if (!reviewCases || reviewCases.length === 0) {
    return <NoRunState />;
  }

  return (
    <LiveReviewTable
      cases={reviewCases}
      periodId={periodId}
      period={period}
      summary={runSummary}
    />
  );
}
